import { useCallback, useEffect, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader, MarkerF } from "@react-google-maps/api";
import { Spinner, Alert } from "react-bootstrap";
import {
	DEFAULT_CENTER,
	GEOCODING_COUNTRY,
	GOOGLE_MAPS_LIBRARIES,
} from "../../pages/canchas/components/mapConfig";

// Interactive location picker for the organizer club form.
//
// The address text is the organizer's primary input; the pin is the precise
// confirmation of it. Geocoding the address only positions the map for
// preview — it never writes lat/lng back. Coordinates are committed solely
// when the organizer drags the marker or clicks the map, so a stored
// lat/lng always means "a human confirmed this spot", never "the geocoder
// guessed". Blank coordinates let the backend run its own geocoding
// fallback on save.

const PICKER_ZOOM = 16;
const ADDRESS_DEBOUNCE_MS = 600;

const mapContainerStyle = { height: "100%", width: "100%" };

const mapOptions = {
	streetViewControl: false,
	mapTypeControl: false,
	fullscreenControl: false,
};

function toFiniteNumber(value) {
	if (value === "" || value == null) return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
}

export default function ClubLocationPicker({
	address,
	lat,
	lng,
	onChange,
	entityLabel = "club",
}) {
	const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

	const { isLoaded, loadError } = useJsApiLoader({
		id: "google-map-script",
		googleMapsApiKey: apiKey,
		libraries: GOOGLE_MAPS_LIBRARIES,
	});

	// Position derived from geocoding the address. Used only to center the map
	// and show a provisional marker while no confirmed pin exists.
	const [geocodedPosition, setGeocodedPosition] = useState(null);
	const [geocoding, setGeocoding] = useState(false);
	const [notFound, setNotFound] = useState(false);
	const mapRef = useRef(null);

	const confirmedLat = toFiniteNumber(lat);
	const confirmedLng = toFiniteNumber(lng);
	const hasConfirmedPin = confirmedLat != null && confirmedLng != null;

	const confirmedPosition = hasConfirmedPin
		? { lat: confirmedLat, lng: confirmedLng }
		: null;

	const trimmedAddress = (address || "").trim();

	// Geocode the address for preview only. Skipped once a pin is confirmed —
	// re-geocoding then would fight the organizer's own placement.
	useEffect(() => {
		if (!isLoaded || hasConfirmedPin || !trimmedAddress) {
			setGeocoding(false);
			setNotFound(false);
			return undefined;
		}
		if (!window.google?.maps?.Geocoder) return undefined;

		let cancelled = false;
		setGeocoding(true);
		setNotFound(false);

		const timer = setTimeout(() => {
			const geocoder = new window.google.maps.Geocoder();
			geocoder.geocode(
				{
					address: trimmedAddress,
					// A bare street address ("Av. Perón 1500") is ambiguous worldwide and
					// returns ZERO_RESULTS. Restricting to Argentina and biasing toward
					// the reference market lets a partially filled form still resolve.
					componentRestrictions: { country: GEOCODING_COUNTRY },
					region: GEOCODING_COUNTRY,
				},
				(results, status) => {
					if (cancelled) return;
					setGeocoding(false);
					if (status === "OK" && results && results[0]) {
						const location = results[0].geometry.location;
						setGeocodedPosition({ lat: location.lat(), lng: location.lng() });
						setNotFound(false);
					} else {
						setGeocodedPosition(null);
						setNotFound(true);
					}
				},
			);
		}, ADDRESS_DEBOUNCE_MS);

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [isLoaded, hasConfirmedPin, trimmedAddress]);

	const commitPosition = useCallback(
		(event) => {
			const nextLat = event?.latLng?.lat?.();
			const nextLng = event?.latLng?.lng?.();
			if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) return;
			onChange({ lat: nextLat, lng: nextLng });
		},
		[onChange],
	);

	const clearPin = useCallback(() => {
		onChange({ lat: "", lng: "" });
	}, [onChange]);

	const center = confirmedPosition || geocodedPosition || DEFAULT_CENTER;

	// Recenter when the target moves while the map is already mounted.
	useEffect(() => {
		if (mapRef.current && (confirmedPosition || geocodedPosition)) {
			mapRef.current.panTo(center);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [center.lat, center.lng]);

	if (!apiKey) {
		return (
			<Alert variant="warning" className="m-0">
				<strong>Google Maps API Key no configurada.</strong>
				<br />
				Definí <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> en tu archivo{" "}
				<code>.env</code>.
			</Alert>
		);
	}

	if (loadError) {
		return (
			<Alert variant="danger" className="m-0">
				No se pudo cargar el mapa. Revisá tu conexión y la API key.
			</Alert>
		);
	}

	if (!isLoaded) {
		return (
			<div className="d-flex align-items-center justify-content-center h-100">
				<Spinner animation="border" size="sm" />
			</div>
		);
	}

	const markerPosition = confirmedPosition || geocodedPosition;

	return (
		<div style={{ position: "relative", height: "100%", width: "100%" }}>
			<GoogleMap
				mapContainerStyle={mapContainerStyle}
				center={center}
				zoom={markerPosition ? PICKER_ZOOM : 12}
				options={mapOptions}
				onLoad={(map) => {
					mapRef.current = map;
				}}
				onUnmount={() => {
					mapRef.current = null;
				}}
				onClick={commitPosition}
			>
				{markerPosition && (
					<MarkerF
						position={markerPosition}
						draggable
						onDragEnd={commitPosition}
						opacity={hasConfirmedPin ? 1 : 0.6}
						title={
							hasConfirmedPin
								? "Ubicación confirmada — arrastrá para ajustar"
								: "Ubicación aproximada — arrastrá o hacé clic para confirmar"
						}
					/>
				)}
			</GoogleMap>

			<div
				style={{
					position: "absolute",
					left: 12,
					bottom: 12,
					right: 12,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 12,
					background: "rgba(255, 255, 255, 0.94)",
					border: "1px solid #e5e7eb",
					borderRadius: 10,
					padding: "8px 12px",
					fontSize: 13,
					pointerEvents: "auto",
				}}
			>
				<span
					style={{
						color: hasConfirmedPin
							? "#166534"
							: notFound
								? "#b45309"
								: "#6b7280",
					}}
				>
					{geocoding
						? "Buscando la dirección…"
						: hasConfirmedPin
							? `Pin confirmado: ${confirmedLat.toFixed(6)}, ${confirmedLng.toFixed(6)}`
							: markerPosition
								? "Ubicación aproximada. Hacé clic en el mapa o arrastrá el pin para confirmarla."
								: notFound
									? `No encontramos esa dirección. Elegí provincia y ciudad, o hacé clic en el mapa para ubicar el ${entityLabel}.`
									: `Ingresá una dirección o hacé clic en el mapa para ubicar el ${entityLabel}.`}
				</span>

				{hasConfirmedPin && (
					<button
						type="button"
						onClick={clearPin}
						style={{
							border: "1px solid #d1d5db",
							background: "#fff",
							borderRadius: 8,
							padding: "4px 10px",
							fontSize: 12,
							cursor: "pointer",
							whiteSpace: "nowrap",
						}}
					>
						Quitar pin
					</button>
				)}
			</div>
		</div>
	);
}
